import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ViewportScroller, AsyncPipe } from '@angular/common';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { HighChartDataBase } from '../../models/high-chart-data-base';
import { ChartPoint } from '../../models/chart-point';
import { LocalDataSource, Angular2SmartTableModule } from 'angular2-smart-table'
import moment from "moment";
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap/tooltip';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'chart-show-table',
    styleUrls: ['./chart-show-table.scss'],
    templateUrl: 'chart-show-table.html',
    host: { 'cid': 'ChartShowTable' },
    imports: [NgbTooltip, Angular2SmartTableModule, AsyncPipe, TranslatePipe]
})
export class ChartShowTableComponent implements OnChanges {
    @Input() chartData: HighChartDataBase;
    @Output() isLoading: EventEmitter<boolean> = new EventEmitter();

    public settings = new BehaviorSubject<any>({});
    public summarySettings = new BehaviorSubject<any>({});
    public data = new BehaviorSubject<any[]>([]);
    public summaryData = new BehaviorSubject<any[]>([]);
    public source: LocalDataSource;
    public summarySource: LocalDataSource;

    constructor(
        private ref: ChangeDetectorRef,
        private translate: TranslateService,
        private viewPortScroller: ViewportScroller
        //private scrollingService: SmoothScrollingService
        ) {

        this.source = new LocalDataSource();
        this.summarySource = new LocalDataSource();

        this.data.subscribe((data) => this.source.load(data));
        this.summaryData.subscribe((summaryData) => this.summarySource.load(summaryData));
    }
    ngOnChanges() {
        this.isLoading.emit(true);

        this.setTableData();

        // ng2-smart-table behöver en digest för att rendera färdigt.
        setTimeout(() => {
            this.ref.detectChanges();
            this.isLoading.emit(false);
        });
    }

    private setTableData() {
        let settings = this.setSettings();
        this.settings.next(settings);

        let sumSettings = this.setSummarySettings(this.setSettings());
        this.summarySettings.next(sumSettings);

        let data = this.setData();
        this.data.next(data);

        let summaryData = this.setSummaryData();
        this.summaryData.next(summaryData);
    }

    private setSettings() {
        let that = this;
        let settings = {};
        let columns = {};
        columns["Name"] = {
            title: this.chartData.xAxisTitle,
            type: 'html',
            valuePrepareFunction: (cell, row) => {
                let colorClass = "";
                let dataArr = that.data.getValue();
                let obj = dataArr.find(x => x.Name == cell);
                let index = dataArr.indexOf(obj);
                if (index >= 0) colorClass = that.chartData.xAxisColors[index];
                return `<span class="${colorClass}">${cell}</span>`;
            }
        };
        settings["hideSubHeader"] = true;
        settings["noDataMessage"] = this.translate.instant('xml-report.table.no-data');
        settings["pager"] = { perPage: 100 };
        settings["actions"] = {
            add: false,
            edit: false,
            delete: false,
            sort: false //PGA konvertering & formatering ej funkar så bra...!
        };

        let series = this.chartData.chartSeries;
        if (series.length > 0) {
            let i = 1;
            switch(this.chartData.chartType) {
                case "boxplot":
                    series.forEach((serie) => {
                        let serieText = series.length > 1 ? serie.name + ", " : "";
                        if (serie.data[0].length > 0) {
                            columns["Value"+1] = { title: serieText + this.translate.instant('xml-report.table.boxplot.minimum') };
                            columns["Value"+2] = { title: serieText + this.translate.instant('xml-report.table.boxplot.lowerQuartile') };
                            columns["Value"+3] = { title: serieText + this.translate.instant('xml-report.table.boxplot.median') };
                            columns["Value"+4] = { title: serieText + this.translate.instant('xml-report.table.boxplot.upperQuartile') };
                            columns["Value"+5] = { title: serieText + this.translate.instant('xml-report.table.boxplot.maximum') };
                        }
                        i++;
                    });
                    break;
                case "funnel":
                    columns["Value"+1] = { title: this.chartData.yAxisTitle };
                    break;
                case "rangecolumn":
                    series.forEach((serie) => {
                        if (serie.data[0]['y']) {
                            columns["Value"+i] = { title: this.getSerieNameSetting(serie.name) };
                        } else {
                            let serieText = series.length > 1 ? serie.name + ", " : "";
                            columns["StartValue"+i] = { title: serieText + this.translate.instant('xml-report.table.rangecolumn.lower') };
                            columns["EndValue"+i] = { title: serieText + this.translate.instant('xml-report.table.rangecolumn.upper') };
                        }
                        i++;
                    });
                    break;
                case "radar":
                    series.forEach((serie) => {
                        columns["Value"+i] = { title: this.getSerieNameSetting(serie.name) };
                        i++;
                    });
                    break;
                default:
                    series.forEach((serie) => {
                        columns["Value"+i] = { title: this.getSerieNameSetting(serie.name) };
                        i++;
                    });
                    if (this.chartData.customDataLabels != null) {
                        columns["Value"+i] = { title: "(ɳ) " + this.chartData.customDataLabelsTitle };
                    }
                    break;
            }
        }
        settings["columns"] = columns;

        return settings;
    }

    private getSerieNameSetting(name: string): string {
        return name.length > 0 ? name : this.chartData.yAxisTitle;
    }

    private setSummarySettings(settings: any) {
        settings.columns.Name.title = this.translate.instant('xml-report.table.summary.header');
        settings["noDataMessage"] = this.translate.instant('xml-report.table.summary.no-sum');
        return settings;
    }

    private setData() {
        let rows = [];
        let series = this.chartData.chartSeries;
        if (series.length > 0) {
            let chartType = this.chartData.chartType;
            let categories = this.chartData.categories;
            this.setDatetimeFormat();
            let i = 0;
            if (categories.length > 0) {
                switch(chartType) {
                    case "boxplot":
                        series[0].data.forEach(data => {
                            let rowItem = {};
                            this.setFirstColumn(rowItem, categories[i]);
                            if (data.length > 0) {
                                let j = 1;
                                data.forEach(value => {
                                    rowItem["Value"+j] = this.formatValue(value);
                                    j++;
                                });
                            }
                            rows.push(rowItem);
                            i++;
                        });
                        break;
                    case "rangecolumn":
                        categories.forEach(category => {
                            let rowItem = {};
                            this.setFirstColumn(rowItem, category);
                            let j = 1;
                            series.forEach(serie => {
                                if (serie.data[i]) {
                                    if (serie.data[i]['y']) {
                                        if (serie.data[i] != undefined) {
                                            this.addValueToRow(rowItem, serie.data[i].y, j);
                                        } else {
                                            rowItem["Value"+j] = "";
                                        }
                                    } else {
                                        rowItem["StartValue"+j] = serie.data[i].high;
                                        if (this.isDate(rowItem["StartValue"+j])) this.convertRowItemToDate(rowItem, "StartValue"+j);
                                        else rowItem["StartValue"+j] = this.formatValue(serie.data[i].high);
                                        rowItem["EndValue"+j] = serie.data[i].low;
                                        if (this.isDate(rowItem["EndValue"+j])) this.convertRowItemToDate(rowItem, "EndValue"+j);
                                        else rowItem["EndValue"+j] = this.formatValue(serie.data[i].low);
                                        j++;
                                    }
                                }
                            });
                            rows.push(rowItem);
                            i++;
                        });
                        break;
                    default:
                        categories.forEach(category => {
                            let rowItem = {};
                            this.setFirstColumn(rowItem, category);
                            let j = 1;
                            series.forEach(serie => {
                                if (serie.data.length == categories.length) {
                                    if (serie.data[i] != undefined) {
                                        let value = (chartType == "line") ? serie.data[i] : serie.data[i].y;
                                        this.addValueToRow(rowItem, value, j);
                                    } else {
                                        rowItem["Value"+j] = "";
                                    }
                                } else {
                                    rowItem["Value"+j] = "";
                                    let result = serie.data.filter(item => item.name == category);
                                    result.forEach(item => {
                                        let itemValue = (chartType == "line") ? item : item.y;
                                        rowItem["Value"+j] += this.formatValue(itemValue) + ", ";
                                    });
                                    rowItem["Value"+j] = rowItem["Value"+j].slice(0, -2);
                                }
                                j++;
                            });
                            if (this.chartData.customDataLabels != null) {
                                //this.customDataLabels[this.categories.indexOf(this.point.name)
                                rowItem["Value"+j] = this.chartData.customDataLabels[i];
                            }
                            rows.push(rowItem);
                            i++;
                        });
                        break;
                }
            } else {
                // Om categories ej används i grafen
                switch(chartType) {
                    case "funnel":
                        series[0].data.forEach(dataItem => {
                            //Visa bara serie 1, resterande är exponentiella linjer med väldigt många punkter
                            let rowItem = {};
                            rowItem["Name"] = dataItem[0];
                            rowItem["Value"+1] = dataItem[1];
                            rows.push(rowItem);
                        });
                        break;
                    default:
                        let longestSerieDataLength = 0;
                        let longestSerieDataIndex = 0;
                        series.forEach((serie, index) => {
                            if (serie.data.length > longestSerieDataLength) {
                                longestSerieDataLength = serie.data.length;
                                longestSerieDataIndex = index;
                            }
                        });

                        series[longestSerieDataIndex].data.forEach((item: ChartPoint, index) => {
                            let rowItem = {};
                            let firstColumnValue = item.name != null ? item.name : item[0];
                            this.setFirstColumn(rowItem, firstColumnValue);
                            let j = 1;
                            let secondColumnValue = item.y != null ? item.y : item[1];
                            this.addValueToRow(rowItem, secondColumnValue, j);
                            if (series.length > 1) {
                                series.forEach((serie, serieIndex) => {
                                    if (serieIndex != longestSerieDataIndex) {
                                        j++;
                                        let rowName = item.name;
                                        let rowNameIsDate = false;
                                        if (this.isDate(rowName)) {
                                            rowNameIsDate = true;
                                            rowName = this.convertToDate(rowName);
                                        }
                                        let serieIndexMatch = false;
                                        if (serie.data[index]) {
                                            let serieName = serie.data[index].name;
                                            if (rowNameIsDate) serieName = this.convertToDate(serieName);
                                            if (rowName == serieName) {
                                                serieIndexMatch = true;
                                                this.addValueToRow(rowItem, serie.data[index].y, j);
                                            }
                                        }
                                        if (!serie.data[index] || !serieIndexMatch) {
                                            // Kollar om det finns en matchning på annat index i serien
                                            serie.data.forEach(dataItem => {
                                                let itemName = dataItem.name;
                                                if (rowNameIsDate) itemName = this.convertToDate(itemName);
                                                if (rowName == itemName) {
                                                    this.addValueToRow(rowItem, dataItem.y, j);
                                                }
                                            });
                                        }
                                    }
                                });
                            }
                            rows.push(rowItem);
                        });
                        break;
                }
            }
        }

        return rows;
    }
    private addValueToRow(rowItem, value, index) {
        rowItem["Value"+index] = this.formatValue(value);
        if (this.isDate(value))
            this.convertRowItemToDate(rowItem, "Value"+index);
    }

    private setSummaryData() {
        let summaryRows = [];
        let series = this.chartData.chartSeries;
        if (series.length > 0) {
            let i = 1;
            if (this.chartData.showTotal) {
                let rowItem = {};
                rowItem["Name"] = this.translate.instant('xml-report.table.summary.total');

                series.forEach(serie => {
                    rowItem["Value"+i] = this.formatValue(serie.serieTotal);
                    i++;
                });
                if (this.chartData.customDataLabels != null) {
                    rowItem["Value"+i] = this.getTotal(this.stringArrToNumberArr(this.chartData.customDataLabels));
                }
                summaryRows.push(rowItem);
                i = 1;
            }
            if (this.chartData.showMedian) {
                let rowItem = {};
                rowItem["Name"] = this.translate.instant('xml-report.table.summary.median');
                series.forEach(serie => {
                    rowItem["Value"+i] = this.formatValue(serie.serieMedian);
                    i++;
                });
                if (this.chartData.customDataLabels != null) {
                    rowItem["Value"+i] = this.getMedian(this.stringArrToNumberArr(this.chartData.customDataLabels));
                }
                summaryRows.push(rowItem);
                i = 1;
            }
            if (this.chartData.showAverage) {
                let rowItem = {};
                rowItem["Name"] = this.translate.instant('xml-report.table.summary.average');
                i = 1;
                series.forEach(serie => {
                    rowItem["Value"+i] = this.formatValue(serie.serieAverage);
                    i++;
                });
                if (this.chartData.customDataLabels != null) {
                    rowItem["Value"+i] = this.getAverage(this.stringArrToNumberArr(this.chartData.customDataLabels));
                }
                summaryRows.push(rowItem);
            }
        }
        return summaryRows;
    }

    private setFirstColumn(rowItem, category) {
        if (this.isDate(category)) {
            rowItem["Name"] = category;
            this.convertRowItemToDate(rowItem, "Name");
        } else if (this.chartData.grouping == this.translate.instant('xml-report.chart.groupings.year')) {
            rowItem["Name"] = category;
        } else {
            rowItem["Name"] = this.formatValue(category);
        }

    }

    public getCellClass({ row, column, value }): any {
        return {
            'classname': value["colorClass"].length > 0
        }
    }



    public isDate(value): boolean {
        if (!value) // Om värdet exempelvis är null/undefined/false
            return false;
        else if (typeof value.getMonth === 'function') // Om värdet redan är av type "DateTime"
            return true;
        else if (this.isTextDate(value)) // Ex. "Januari"
            return false;
        else if (this.containsNumbers(value.toString()) == false) // Värdet innehåller inga siffror
            return false;
        else if (value.length < 6) // Ett datum kan inte vara kortare än 6 tecken
            return false;

        let momentDate = moment(value);
        let minDate = moment("2008-01-01 00:00:00");
        let maxDate = moment();
        return momentDate > minDate && momentDate < maxDate;
    }

    public isTextDate(value): boolean {
        if (value.toString().indexOf(this.translate.instant('months.01')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.02')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.03')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.04')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.05')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.06')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.07')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.08')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.09')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.10')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.11')) > -1 ||
            value.toString().indexOf(this.translate.instant('months.12')) > -1)
            return true;
        else
            return false;
    }

    private containsNumbers(value): boolean {
        return /\d/.test(value.toString());
    }

    private dateFormat = "YYYY-MM-DD";
    private isDateWithTime(dateobj: Date): boolean {
        let hour = dateobj.getHours();
        let minute = dateobj.getMinutes();
        let second = dateobj.getSeconds();
        return hour != 0 && minute != 0 && second != 0;
    }
    private setDatetimeFormat() {
        let series = this.chartData.chartSeries;
        series.forEach((serie) => {
            for (var i = 0; i < serie.data.length; i += 1) {
                let value = serie.data[i]['x'];
                if (this.isDate(value) && this.isDateWithTime(value)) {
                    this.dateFormat = "YYYY-MM-DD HH:mm";
                    return;
                }
            }
        });
        return;
    }

    private convertRowItemToDate(rowItem, property: string) {
        rowItem[property] = this.convertToDate(rowItem[property]);
    }
    private convertToDate(item) {
        return moment(item).locale(this.translate.currentLang).format(this.dateFormat);
    }

    private formatValue(value) {
        if (!value || value.length == 0 || Number.isNaN(Number(value)) || typeof value === 'string') {
            return value;
        } else {
            let formattedValue = parseFloat(value).toLocaleString(this.translate.currentLang, { maximumFractionDigits: this.chartData.yAxisDecimals, minimumFractionDigits: this.chartData.yAxisDecimals });
            return formattedValue;
        }

        /*
         * Sorteringen fungerar halvdant trots konvertering -> Tillsvidare: Visa talen rätt formaterade utan sorteringsfunktion!
         *
        if (!value || value.length == 0 || Number.isNaN(Number(value))) {
            return value;
        } else {
            let formattedValue = Number(new Intl.NumberFormat(this.translate.currentLang, { maximumFractionDigits: this.chartData.yAxisDecimals, minimumFractionDigits: this.chartData.yAxisDecimals }) //ex. 'en-US': 10000.54 => 10,000.54
                                            .format(value).replace(",", ""));
            return formattedValue;
        }
        */
    }



    private stringArrToNumberArr(stringArr: string[]): number[] {
        var numberArr: number[] = [];
        for(var i = 0; i < stringArr.length; i++)
            numberArr.push(Number(stringArr[i]));
        return numberArr;
    }

    private getTotal(values) {
        var num = 0;
        for (var i = 0, l = values.length; i < l; i++)
            num += values[i];
        return num;
    }

    private getAverage(values) {
        var total = this.getTotal(values);
        return (total / values.length).toFixed(2);
    }

    private getMedian(values) {
        values.sort(function(a,b){
            return a-b;
        });
        if (values.length === 0) return 0;
        var half = Math.floor(values.length / 2);
        return (values.length % 2 ? values[half] : (values[half - 1] + values[half]) / 2.0);
    }



    public showSummary(): boolean {
        return this.chartData.showTotal || this.chartData.showMedian || this.chartData.showAverage;
    }

    public goToSummary():void {
        this.viewPortScroller.scrollToAnchor('summary');
    }
}
